import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { TEAM, TEAM_CAPABILITIES } from "@/lib/labContent";

export function MeetTheTeam() {
  // If TEAM is empty, render the alternate "team capabilities" block.
  if (!TEAM || TEAM.length === 0) {
    return (
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-600">Our team</span>
          <h2 className="font-display mt-3 text-3xl lg:text-4xl font-bold text-slate-900 leading-[1.1]">
            Experienced lab technicians & customer-care specialists.
          </h2>
          <ul className="mt-7 grid sm:grid-cols-2 gap-3">
            {TEAM_CAPABILITIES.map((c) => (
              <li key={c} className="flex items-start gap-2.5 p-4 rounded-2xl bg-white border border-slate-100">
                <CheckCircle2 className="w-5 h-5 mt-0.5 text-cyan-500 flex-shrink-0" />
                <span className="text-sm text-slate-700 leading-snug">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    );
  }

  return (
    <section className="py-14 lg:py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 mb-10">
          <div className="lg:col-span-7">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-600">Meet the lab team</span>
            <h2 className="font-display mt-3 text-3xl lg:text-4xl font-bold text-slate-900 leading-[1.1]">
              People who care about what's in your water.
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              The lab technicians and customer-care specialists who handle every sample from receipt to result. Trained in recognised laboratory methodologies and dedicated to plain-English answers.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TEAM.map((m, i) => (
            <motion.article
              key={m.name + i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.45, delay: i * 0.07 }}
              data-testid={`team-card-${i}`}
              className="group bg-white rounded-3xl border border-slate-100 p-6 hover:shadow-xl hover:-translate-y-1 hover:border-cyan-200 transition-all"
            >
              <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-sky-600 flex items-center justify-center overflow-hidden">
                {m.photo ? (
                  <img src={m.photo} alt={m.name} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <span className="font-display text-white text-2xl font-bold tracking-tight">{m.initials}</span>
                )}
              </div>
              <h3 className="mt-5 font-display text-lg font-semibold text-slate-900 leading-tight">{m.name}</h3>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-600 mt-1">{m.role}</div>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{m.bio}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
